import React from 'react';
import { RESOURCE_TYPE_LABELS } from '../../../services/vendorService';
import type { VendorResource } from '../../../services/vendorService';
import ResourceRatesCell from './ResourceRatesCell';

/**
 * M27 - what is inside a bundle, without leaving the list.
 *
 * The toggle shows the component count in the row ("3 resources"); expanding it lists each
 * component with its type, unit and rate. Rendered through DataTable's `renderExpanded`, so it
 * works the same on the desktop table and the phone card layout.
 */
export const BundleComponentsToggle: React.FC<{
  resource: VendorResource;
  expanded: boolean;
  onToggle: () => void;
}> = ({ resource, expanded, onToggle }) => {
  const count = resource.bundleComponents?.length ?? 0;
  return (
    <button
      type="button"
      className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary hover:tw-underline focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-line-focus"
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Hide' : 'Show'} the ${count} resource${count === 1 ? '' : 's'} in ${resource.name}`}
      onClick={(e) => {
        // Rows may navigate on click; opening the contents must not.
        e.stopPropagation();
        onToggle();
      }}
    >
      <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} tw-text-xs`} aria-hidden="true" />
      {count} resource{count === 1 ? '' : 's'}
    </button>
  );
};

export const BundleComponentsList: React.FC<{ resource: VendorResource }> = ({ resource }) => {
  const components = resource.bundleComponents ?? [];
  if (!components.length) {
    return <div className="tw-text-sm tw-text-content-muted">This bundle has no components.</div>;
  }
  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-text-sm tw-tabular-nums">
        <thead>
          <tr className="tw-text-left tw-text-xs tw-font-semibold tw-text-content-secondary">
            <th className="tw-py-1 tw-pr-4 tw-font-semibold">Component</th>
            <th className="tw-py-1 tw-pr-4 tw-font-semibold">Type</th>
            <th className="tw-py-1 tw-pr-4 tw-font-semibold">Units of Measure</th>
            <th className="tw-py-1 tw-pr-4 tw-font-semibold">Qty</th>
            <th className="tw-py-1 tw-font-semibold">Rate</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => (
            <tr key={c.id} className="tw-border-t tw-border-line">
              <td className="tw-py-1 tw-pr-4">{c.componentResource.name}</td>
              <td className="tw-py-1 tw-pr-4">{RESOURCE_TYPE_LABELS[c.componentResource.resourceType]}</td>
              <td className="tw-py-1 tw-pr-4">{c.componentResource.unit ?? '–'}</td>
              <td className="tw-py-1 tw-pr-4">{c.qty}</td>
              <td className="tw-py-1">
                <ResourceRatesCell rates={c.componentResource.rates} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
